// src/components/AssetDetailsTable.tsx
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { GetIcons } from 'views/SurvivalAnalysis/Helper/Icons/GetIcons.function';
import componentsImage from 'views/SurvivalAnalysis/Helper/Icons/componenent.png';
import { ThemePalette } from 'views/SurvivalAnalysis/Helper/Icons/theme.squares';
import { type ComponentDetails } from 'types/squares.type';
import { SVG } from 'components/Asset/SVG';

interface AssetDetailsTableProps {
  enableDarkTheme: boolean;
  details: ComponentDetails[];
}

const AssetDetailsTable: React.FC<AssetDetailsTableProps> = ({
  enableDarkTheme,
  details,
}) => {
  const getBorderColor = (criticality: string): string => {
    switch (criticality) {
      case 'low':
        return ThemePalette.typography.success;
      case 'med':
        return ThemePalette.typography.warning;
      default:
        return ThemePalette.typography.danger;
    }
  };

  return (
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table
        style={{
          background: enableDarkTheme
            ? ThemePalette.dark.tableBackground
            : ThemePalette.light.tableBackground,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Component
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Service Frequency
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Age (Last Maintenance)
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Manufacturer Rec. Date
            </TableCell>
            <TableCell
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.lightGrey
                  : ThemePalette.typography.grey,
              }}
            >
              Squares Recommended Date
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {details.slice(0, 3).map(
            (
              detail,
              index // Show only the top 3 items
            ) => (
              <TableRow
                key={`${detail.bumper_number}-${index}`}
                sx={{
                  borderLeft: `4px solid ${getBorderColor(detail.criticality)}`,
                }}
              >
                {/* Component Details */}
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <GetIcons
                      icon={componentsImage}
                      enableDarkTheme={enableDarkTheme}
                    />
                    <Box>
                      <Typography
                        style={{
                          color: enableDarkTheme
                            ? ThemePalette.typography.white
                            : ThemePalette.typography.black,
                        }}
                        variant="body2"
                      >
                        {detail.component}
                      </Typography>
                      <Typography
                        style={{
                          color: enableDarkTheme
                            ? ThemePalette.typography.lightGrey
                            : ThemePalette.typography.grey,
                        }}
                        variant="body2"
                      >
                        {detail.sub_component}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Ideal Change */}
                <TableCell>
                  <Box>
                    {detail.freq_calendar_days && (
                      <Typography
                        style={{
                          color: enableDarkTheme
                            ? ThemePalette.typography.white
                            : ThemePalette.typography.black,
                        }}
                        variant="body2"
                      >
                        <CalendarToday sx={{ fontSize: '12px' }} />
                        {` Every ${detail.freq_calendar_days}`}{' '}
                        {detail.freq_engine_hours &&
                          detail.freq_calendar_days && <span>or</span>}
                      </Typography>
                    )}

                    {detail.freq_engine_hours && (
                      <Typography
                        style={{
                          color: enableDarkTheme
                            ? ThemePalette.typography.white
                            : ThemePalette.typography.black,
                        }}
                        variant="body2"
                      >
                        <SVG name="streetIcon" />
                        {` Every ${detail.freq_engine_hours}`}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Age (Last Maintenance) */}
                <TableCell>
                  <Typography
                    style={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.white
                        : ThemePalette.typography.black,
                    }}
                    variant="body2"
                  >
                    <SVG name="engineLight" /> {Number(detail.age_in_days) * 24}{' '}
                    Hours
                  </Typography>
                  <Typography
                    style={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.lightGrey
                        : ThemePalette.typography.grey,
                    }}
                    variant="body2"
                  >
                    <CalendarToday sx={{ fontSize: '12px' }} />{' '}
                    {new Date(
                      new Date().setDate(
                        new Date().getDate() - Number(detail.age_in_days)
                      )
                    ).toLocaleDateString()}
                  </Typography>
                </TableCell>

                {/* Manufacturer Rec. Date */}
                <TableCell>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                  >
                    <Typography
                      style={{
                        color: enableDarkTheme
                          ? ThemePalette.typography.white
                          : ThemePalette.typography.black,
                      }}
                      variant="body1"
                    >
                      {detail.manufacture_recommended_date}
                    </Typography>
                    <Typography
                      style={{
                        color: enableDarkTheme
                          ? ThemePalette.typography.lightGrey
                          : ThemePalette.typography.grey,
                      }}
                      variant="subtitle2"
                      color="grey"
                    >
                      {(() => {
                        const daysDifference = Math.ceil(
                          (new Date(
                            detail.manufacture_recommended_date
                          ).getTime() -
                            new Date().getTime()) /
                            (1000 * 60 * 60 * 24)
                        );

                        if (daysDifference > 0) {
                          return `${daysDifference} Days from now`;
                        } else if (daysDifference < 0) {
                          return `${Math.abs(daysDifference)} Days overdue`;
                        } else {
                          return 'Immediate';
                        }
                      })()}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Squares Recommended Date */}
                <TableCell>
                  <Typography
                    style={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.white
                        : ThemePalette.typography.black,
                    }}
                    variant="body2"
                  >
                    {detail.squares_recommended_date}
                  </Typography>
                  <Typography
                    style={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.lightGrey
                        : ThemePalette.typography.grey,
                    }}
                    variant="subtitle2"
                    color="grey"
                  >
                    {(() => {
                      const daysDifference = Math.ceil(
                        (new Date(detail.squares_recommended_date).getTime() -
                          new Date().getTime()) /
                          (1000 * 60 * 60 * 24)
                      );

                      if (daysDifference > 0) {
                        return `${daysDifference} Days from now`;
                      } else if (daysDifference < 0) {
                        return `${Math.abs(daysDifference)} Days overdue`;
                      } else {
                        return 'Immediate';
                      }
                    })()}
                  </Typography>
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AssetDetailsTable;
